// API service layer for communicating with the backend
// Use environment variable for production, fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

class ApiService {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log('=== API REQUEST ===');
    console.log('URL:', url);
    console.log('Options:', options);

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authorization header if token exists
    if (this.token) {
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      console.log('Making fetch request to:', url);
      const response = await fetch(url, config);
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.detail || data.Message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // Client API methods
  async createClient(clientData) {
    return this.request('/client/createClient', {
      method: 'POST',
      body: JSON.stringify(clientData),
    });
  }

  async getClientProfile(phoneNumber) {
    return this.request('/client/getProfile', {
      method: 'POST',
      body: JSON.stringify({ client_phone_number: phoneNumber }),
    });
  }

  async deleteClient(clientId) {
    return this.request('/client/deleteClient', {
      method: 'DELETE',
      body: JSON.stringify({ client_id: clientId }),
    });
  }

  // Pet API methods
  async createPet(petData) {
    return this.request('/client/pet/createClientPet', {
      method: 'POST',
      body: JSON.stringify(petData),
    });
  }

  async getClientPets(clientId) {
    return this.request(`/client/pet/getClientPets?client_id=${encodeURIComponent(clientId)}`, {
      method: 'POST',
    });
  }

  async getProfile(clientId) {
    return this.request(`/client/getProfileById?client_id=${encodeURIComponent(clientId)}`, {
      method: 'POST',
    });
  }

  async getClientProfile(clientId) {
    return this.request(`/client/profile/${clientId}`, {
      method: 'GET',
    });
  }

  async updateClientProfile(clientId, profileData) {
    return this.request(`/client/profile/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async getClientSessions(clientId) {
    return this.request(`/client/sessions/${clientId}`, {
      method: 'GET',
    });
  }

  async updatePet(petId, petData) {
    return this.request(`/client/pet/updateClientPet?pet_id=${encodeURIComponent(petId)}`, {
      method: 'PUT',
      body: JSON.stringify(petData),
    });
  }

  async deletePet(petId) {
    return this.request('/client/pet/deleteClientPet', {
      method: 'DELETE',
      body: JSON.stringify({ pet_id: petId }),
    });
  }

  // Session request methods
  async createSessionRequest(requestData) {
    console.log('=== API CALL: createSessionRequest ===');
    console.log('Request data:', requestData);

    return this.request('/session/createRequest', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
  }

  async getActiveTrainingRequests(clientId) {
    return this.request(`/session/getActiveRequests?client_id=${encodeURIComponent(clientId)}`, {
      method: 'GET'
    });
  }

  async createCheckoutSession(requestIdOrPayload) {
    const payload = typeof requestIdOrPayload === 'object'
      ? requestIdOrPayload
      : { request_id: requestIdOrPayload };
    return this.request('/payment/createCheckoutSession', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async verifyCheckoutSession(verifyData) {
    return this.request('/payment/verifySession', {
      method: 'POST',
      body: JSON.stringify(verifyData)
    });
  }

  async cancelSessionRequest(requestId) {
    return this.request(`/session/cancelRequest?request_id=${encodeURIComponent(requestId)}`, {
      method: 'DELETE'
    });
  }

  async registerSessionJoin(requestId, role) {
    return this.request('/session/registerJoin', {
      method: 'POST',
      body: JSON.stringify({ request_id: requestId, role })
    });
  }

  // Trainer API methods
  async createTrainer(trainerData) {
    return this.request('/trainer/createTrainer', {
      method: 'POST',
      body: JSON.stringify(trainerData),
    });
  }

  async submitBackgroundCheck(data) {
    return this.request('/trainer/submitBackgroundCheck', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTrainerID(email) {
    console.log('=== API CALL: getTrainerID ===');
    console.log('Email:', email);
    console.log('URL:', `/trainer/getTrainerID?trainer_email=${email}`);
    return this.request(`/trainer/getTrainerID?trainer_email=${email}`, {
      method: 'POST',
    });
  }

  async getTrainerProfile(trainerId) {
    console.log('=== API CALL: getTrainerProfile ===');
    console.log('Trainer ID:', trainerId);
    console.log('URL:', `/trainer/getProfile?trainer_id=${trainerId}`);
    return this.request(`/trainer/getProfile?trainer_id=${trainerId}`, {
      method: 'GET',
    });
  }

  async createSupportTicket(ticketData) {
    return this.request('/support/tickets', {
      method: 'POST',
      body: JSON.stringify(ticketData),
    });
  }

  async editTrainer(trainerId, updateData) {
    // Convert the updateData to the format expected by the backend
    const params = new URLSearchParams();
    params.append('trainer_id', trainerId);

    // Map frontend field names to backend parameter names
    if (updateData.first_name) params.append('trainer_first_name', updateData.first_name);
    if (updateData.last_name) params.append('trainer_last_name', updateData.last_name);
    if (updateData.email) params.append('trainer_email', updateData.email);
    if (updateData.phone_number) params.append('trainer_phone_number', updateData.phone_number);
    if (updateData.address) params.append('trainer_address', updateData.address);
    if (updateData.specialties) params.append('specialties', updateData.specialties);
    if (updateData.experience_years) params.append('experience_years', updateData.experience_years);
    if (updateData.hourly_rate) params.append('hourly_rate', updateData.hourly_rate);
    if (updateData.bio) params.append('bio', updateData.bio);

    console.log('Edit trainer URL:', `/trainer/editTrainer?${params.toString()}`);
    console.log('Edit trainer params:', params.toString());

    return this.request(`/trainer/editTrainer?${params.toString()}`, {
      method: 'PUT',
    });
  }

  async deleteTrainer(trainerId) {
    return this.request('/trainer/deleteTrainer', {
      method: 'DELETE',
      body: JSON.stringify({ trainer_id: trainerId }),
    });
  }

  async deleteUserByUsername(username) {
    return this.request(`/admin/deleteUserByUsername?username=${encodeURIComponent(username)}`, {
      method: 'DELETE'
    });
  }

  async pushTrainerReview(reviewData) {
    return this.request('/trainer/pushReview', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  async submitSessionReview(reviewData) {
    return this.request('/session/submitReview', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  async getSessionRating(sessionRequestId) {
    return this.request(`/session/getRating?session_request_id=${encodeURIComponent(sessionRequestId)}`, {
      method: 'GET',
    });
  }

  async getTrainerScore(trainerId) {
    console.log('=== API CALL: getTrainerScore ===');
    console.log('Trainer ID:', trainerId);
    console.log('URL:', `/trainer/getScore?trainer_id=${trainerId}`);
    return this.request(`/trainer/getScore?trainer_id=${trainerId}`, {
      method: 'GET',
    });
  }

  async getTrainerProfile(trainerId) {
    return this.request(`/trainer/profile/${trainerId}`, {
      method: 'GET',
    });
  }

  async updateTrainerProfile(trainerId, profileData) {
    return this.request(`/trainer/profile/${trainerId}`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async adminLogin(username, password) {
    return this.request('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async uploadTrainerDocument(trainerId, documentType, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/trainer/uploadDocument?trainer_id=${trainerId}&document_type=${documentType}`, {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for FormData
    });
  }

  async deleteTrainerDocument(trainerId, documentType) {
    return this.request(`/trainer/deleteDocument?trainer_id=${trainerId}&document_type=${documentType}`, {
      method: 'DELETE'
    });
  }

  // Session Management
  async createSession(sessionData) {
    return this.request('/session/create', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  }

  async updateSession(sessionData) {
    return this.request('/session/update', {
      method: 'PUT',
      body: JSON.stringify(sessionData),
    });
  }

  async updateSessionPayment(paymentData) {
    return this.request('/session/payment', {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }

  async getSession(sessionId) {
    return this.request(`/session/${sessionId}`, {
      method: 'GET',
    });
  }

  async generateInvoice(invoiceData) {
    return this.request('/invoice/generate', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    });
  }

  // Reference Video Management
  async uploadReferenceVideo(formData) {
    return this.request('/reference-video/upload', {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for FormData
    });
  }

  async getReferenceVideos(sessionId) {
    return this.request(`/reference-videos/${sessionId}`, {
      method: 'GET',
    });
  }

  async deleteReferenceVideo(videoId) {
    return this.request(`/reference-video/${videoId}`, {
      method: 'DELETE',
    });
  }

  // Trainer Payment Management
  async getTrainerPayments(trainerId) {
    return this.request(`/trainer/${trainerId}/payments`, {
      method: 'GET',
    });
  }

  async getTrainerEarningsSummary(trainerId, year) {
    return this.request(`/trainer/${trainerId}/earnings-summary?year=${year}`, {
      method: 'GET',
    });
  }

  async processTrainerPayout(trainerId, payoutData) {
    return this.request(`/trainer/${trainerId}/payout`, {
      method: 'POST',
      body: JSON.stringify(payoutData),
    });
  }

  // Admin Payment Management
  async getTrainer1099Reports(year) {
    return this.request(`/admin/trainer-1099-reports/${year}`, {
      method: 'GET',
    });
  }

  // Session API methods
  async requestSession(clientId, trainerId) {
    return this.request('/session/requestSession', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, trainer_id: trainerId }),
    });
  }

  async acceptSession(sessionRequestId) {
    return this.request(`/session/acceptSession?session_request_id=${encodeURIComponent(sessionRequestId)}`, {
      method: 'POST',
      body: JSON.stringify({ session_request_id: sessionRequestId }),
    });
  }

  async declineSession(sessionRequestId) {
    return this.request('/session/declineSession', {
      method: 'POST',
      body: JSON.stringify({ session_request_id: sessionRequestId }),
    });
  }

  async getSessionRequestDetails(requestId) {
    return this.request(`/session/getRequestDetails/${encodeURIComponent(requestId)}`, {
      method: 'GET'
    });
  }

  async createOrUpdateDeliverables(deliverablesData) {
    return this.request('/session/deliverables', {
      method: 'POST',
      body: JSON.stringify(deliverablesData)
    });
  }

  async getDeliverables(sessionRequestId) {
    return this.request(`/session/deliverables/${encodeURIComponent(sessionRequestId)}`);
  }

  async updateSessionFromRequest(requestId, updateData) {
    return this.request(`/session/updateFromRequest/${encodeURIComponent(requestId)}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }


  async startSession(sessionRequestId, notes = '') {
    return this.request('/session/startSession', {
      method: 'POST',
      body: JSON.stringify({ session_request_id: sessionRequestId, notes }),
    });
  }

  async endSession(requestId, notes = '') {
    return this.request(`/session/endSession?request_id=${encodeURIComponent(requestId)}`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async getClientSessionHistory(clientId) {
    return this.request('/session/getClientSessionHistory', {
      method: 'GET',
      body: JSON.stringify({ client_id: clientId }),
    });
  }

  // Get all trainers from backend
  async getAllTrainers() {
    return this.request('/trainer/getAllTrainers', {
      method: 'GET'
    });
  }

  // Get only active trainers from backend
  async getActiveTrainers() {
    return this.request('/trainer/getActiveTrainers', {
      method: 'GET'
    });
  }

  // Make trainer active
  async makeTrainerActive(trainerId, availabilityData = {}) {
    const params = new URLSearchParams();
    params.append('trainer_id', trainerId);
    if (availabilityData.available_from) {
      params.append('available_from', availabilityData.available_from);
    }
    if (availabilityData.available_until) {
      params.append('available_until', availabilityData.available_until);
    }

    console.log('=== API CALL: makeTrainerActive ===');
    console.log('Trainer ID:', trainerId);
    console.log('Availability Data:', availabilityData);
    console.log('URL:', `/session/makeTrainerActive?${params.toString()}`);

    return this.request(`/session/makeTrainerActive?${params.toString()}`, {
      method: 'POST',
    });
  }

  // Make trainer inactive
  async makeTrainerInactive(trainerId) {
    const params = new URLSearchParams();
    params.append('trainer_id', trainerId);

    console.log('=== API CALL: makeTrainerInactive ===');
    console.log('Trainer ID:', trainerId);
    console.log('URL:', `/session/makeTrainerInactive?${params.toString()}`);

    return this.request(`/session/makeTrainerInactive?${params.toString()}`, {
      method: 'POST',
    });
  }

  // Get trainer session requests
  async getTrainerRequests(trainerId) {
    console.log('=== API CALL: getTrainerRequests ===');
    console.log('Trainer ID:', trainerId);

    return this.request(`/trainer/${encodeURIComponent(trainerId)}/requests`, {
      method: 'GET',
    });
  }

  // Authentication methods
  async requestEmailVerification(email, userType) {
    return this.request('/auth/request-email-verification', {
      method: 'POST',
      body: JSON.stringify({ email, user_type: userType })
    });
  }

  async verifyEmailCode(email, verificationCode) {
    return this.request('/auth/verify-email-code', {
      method: 'POST',
      body: JSON.stringify({ email, verification_code: verificationCode })
    });
  }

  async register(username, email, password, name = '', userType, verificationToken) {
    const requestData = {
      username,
      email,
      password,
      name,
      user_type: userType,
      verification_token: verificationToken
    };
    console.log('API Service - Registration request data:', requestData);

    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
  }

  async login(username, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
  }

  async setUserType(userType) {
    return this.request('/auth/set-user-type', {
      method: 'POST',
      body: JSON.stringify({ user_type: userType })
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me', {
      method: 'GET'
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST'
    });
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword })
    });
  }

  // Photo upload methods
  async uploadTrainerProfilePhoto(trainerId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/trainer/uploadProfilePhoto?trainer_id=${trainerId}`, {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for FormData
    });
  }

  async uploadClientProfilePhoto(clientId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/client/uploadProfilePhoto?client_id=${clientId}`, {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for FormData
    });
  }

  async uploadPetPhoto(petId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/client/uploadPetPhoto?pet_id=${petId}`, {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for FormData
    });
  }

  // Trainer setup options methods
  async getTrainingPhilosophyOptions() {
    return this.request('/trainer/options/training-philosophy', {
      method: 'GET'
    });
  }

  async getAreasOfExpertiseOptions() {
    return this.request('/trainer/options/areas-of-expertise', {
      method: 'GET'
    });
  }

  async getCredentialsOptions() {
    return this.request('/trainer/options/credentials', {
      method: 'GET'
    });
  }

  // Trainer registration payment methods
  async createTrainerRegistrationCheckout(data) {
    return this.request('/payment/createTrainerRegistrationCheckout', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async verifyTrainerRegistrationPayment(data) {
    return this.request('/payment/verifyTrainerRegistrationPayment', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Stripe Connect methods
  async createConnectAccount(trainerId) {
    return this.request('/payment/createConnectAccount', {
      method: 'POST',
      body: JSON.stringify({ trainer_id: trainerId })
    });
  }

  async createOnboardingLink(trainerId) {
    return this.request('/payment/createOnboardingLink', {
      method: 'POST',
      body: JSON.stringify({ trainer_id: trainerId })
    });
  }

  async getConnectAccountStatus(trainerId) {
    return this.request(`/payment/connectAccountStatus/${encodeURIComponent(trainerId)}`, {
      method: 'GET'
    });
  }

  // Admin methods
  async getPendingTrainerApplications() {
    return this.request('/admin/getPendingTrainerApplications', {
      method: 'GET'
    });
  }

  async acceptTrainerApplication(trainerId) {
    return this.request('/admin/acceptTrainerApplication', {
      method: 'POST',
      body: JSON.stringify({ trainer_id: trainerId })
    });
  }

  async denyTrainerApplication(trainerId, reason = '') {
    return this.request('/admin/denyTrainerApplication', {
      method: 'POST',
      body: JSON.stringify({ trainer_id: trainerId, reason })
    });
  }

  async getAllClients() {
    return this.request('/admin/getAllClients', {
      method: 'GET'
    });
  }

  async getAllSessions(trainerSearch = null) {
    let url = '/admin/getAllSessions';
    if (trainerSearch) {
      url += `?trainer_search=${encodeURIComponent(trainerSearch)}`;
    }
    return this.request(url, {
      method: 'GET'
    });
  }

  async getAdminStats(startDate = null, endDate = null) {
    let url = '/admin/getStats';
    const params = [];
    if (startDate) params.push(`start_date=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`end_date=${encodeURIComponent(endDate)}`);
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return this.request(url, {
      method: 'GET'
    });
  }

  async getAllTrainersForAdmin() {
    return this.request('/admin/getAllTrainersForAdmin', {
      method: 'GET'
    });
  }

  async getBilling() {
    return this.request('/admin/getBilling', {
      method: 'GET'
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
